export interface Product {
  _id: string;
  name: string;
  nameHe: string;
  orderName: string;
  description?: string;
  descriptionHe?: string;
  price: number;
  image?: string;
  category: Category;
  options: ProductOption[];
  isAvailable: boolean;
  preparationTime?: number;
  sortOrder: number;
  tags?: string[];
}

export interface Category {
  _id: string;
  name: string;
  nameHe: string;
  description?: string;
  descriptionHe?: string;
  image?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface ProductOption {
  _id?: string;
  name: string;
  nameHe: string;
  orderName: string;
  displayMode: DisplayMode;
  required: boolean;
  values: OptionValue[];
  minCount?: number;
  maxCount?: number;
  sortOrder: number;
  placeholder?: string;
  placeholderHe?: string;
  // If true, this option will be used to generate a price table in the menu
  forPriceTable?: boolean;
}

export interface OptionValue {
  _id?: string;
  name: string;
  nameHe: string;
  value?: string;  // Deprecated: use name instead
  valueHe?: string;  // Deprecated: use nameHe instead
  image?: string;
  priceModifier: number;
  isDefault?: boolean;
  isAvailable?: boolean;
  sortOrder: number;
}

export enum DisplayMode {
  INLINE_OPTIONS = 'inline-options',
  MODAL_SELECT = 'modal-select',
  CHECKMARK = 'checkmark',
  PLUS_MINUS_LIST = 'plus-minus-list',
  TEXT_INPUT = 'text-input',
  TEXT_AREA = 'text-area',
}

export enum OrderType {
  DELIVERY = 'delivery',
  SIT_IN = 'sit_in',
  TAKE_AWAY = 'take_away',
}

export interface CartItem {
  productId: string;
  productName: string;
  productNameHe: string;
  productImage?: string;
  quantity: number;
  unitPrice: number;
  selectedOptions: SelectedOption[];
  specialInstructions?: string;
  itemTotal: number;
}

export interface SelectedOption {
  optionId?: string;
  optionName: string;
  optionNameHe: string;
  displayMode: DisplayMode;
  selectedValue?: string;
  selectedValueHe?: string;
  quantity?: number;
  textValue?: string;
  priceModifier: number;
}

export interface CartState {
  orderType: OrderType;
  items: CartItem[];
  guestInfo?: {
    name: string;
    phone: string;
    email?: string;
  };
}

export interface Order {
  _id: string;
  orderNumber: string;
  customer?: any;
  orderType: OrderType;
  isGuestOrder: boolean;
  guestName?: string;
  guestPhone?: string;
  guestEmail?: string;
  items: OrderItem[];
  subtotal: number;
  deliveryFee: number;
  discount: number;
  total: number;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod?: string;
  deliveryAddress: DeliveryAddress;
  deliveryAgent?: any;
  estimatedDeliveryTime?: Date;
  actualDeliveryTime?: Date;
  customerNote?: string;
  storeNote?: string;
  refundReason?: string;
  refundAmount?: number;
  statusHistory: StatusHistoryEntry[];
  orderOptions: SelectedOrderOption[];
  createdAt: Date;
  updatedAt: Date;
}

export interface OrderItem {
  product: string;
  productName: string;
  productNameHe: string;
  quantity: number;
  unitPrice: number;
  selectedOptions: OrderItemOption[];
  specialInstructions?: string;
  itemTotal: number;
}

export interface OrderItemOption {
  optionName: string;
  optionNameHe: string;
  selectedValue: string;
  selectedValueHe: string;
  priceModifier: number;
  quantity?: number;
}

export interface DeliveryAddress {
  street: string;
  city: string;
  apartment?: string;
  floor?: string;
  postalCode?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  deliveryInstructions?: string;
}

export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  PREPARING = 'preparing',
  READY_FOR_PICKUP = 'ready_for_pickup',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
}

export enum PaymentStatus {
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

export interface StatusHistoryEntry {
  status: string;
  timestamp: Date;
  note?: string;
}

export interface SelectedOrderOption {
  orderOption: string;
  optionName: string;
  optionNameHe: string;
  inputType: string;
  textValue?: string;
  selectedValue?: string;
  selectedLabel?: string;
  selectedLabelHe?: string;
  selectedValues?: Array<{ value: string; label: string; labelHe: string }>;
}

export interface OrderOption {
  _id: string;
  name: string;
  nameHe: string;
  orderName: string;
  displayMode: DisplayMode;
  required: boolean;
  values: OptionValue[];
  minCount?: number;
  maxCount?: number;
  sortOrder: number;
  placeholder?: string;
  placeholderHe?: string;
  isActive: boolean;
}

export interface Page {
  _id: string;
  slug: string;
  title: string;
  titleHe: string;
  content: string;
  contentHe: string;
  description?: string;
  descriptionHe?: string;
  image?: string;
  category: PageCategory;
  status: PageStatus;
  sortOrder: number;
  metadata?: {
    metaTitle?: string;
    metaTitleHe?: string;
    metaDescription?: string;
    metaDescriptionHe?: string;
    keywords?: string[];
  };
}

export enum PageCategory {
  TOP_MENU = 'top-menu',
  FOOTER_INFO = 'footer-info',
  FOOTER_LEGAL = 'footer-legal',
  FOOTER_CONTACT = 'footer-contact',
}

export enum PageStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
}

export interface Favorite {
  _id: string;
  customer: string;
  name: string;
  product: Product;
  selectedOptions: Record<string, any>;
  specialInstructions?: string;
  createdAt: Date;
  updatedAt: Date;
}
